import { motion } from 'framer-motion'
import styled from 'styled-components'

export const FLOATING_DETAIL_BACKDROP_Z = 100
export const FLOATING_DETAIL_CARD_Z = 101

export const DanknessContainer = styled.div`
  background: ${({ theme }) => theme.colors.menuBg};
  min-height: 100%;
  width: 100%;
  height: 100%;
`

export const StyledColumnLayoutWrapper = styled.div`
  position: relative;
  display: flex;
  height: 100%;
  max-height: 100%;
  width: 100%;
  flex-direction: column;
  background: ${({ theme }) => theme.colors.neutralGray};
  margin: auto;
  overflow: hidden;
  border-radius: ${({ theme }) => theme.borderRadius.md}rem;
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;

  @media (max-width: ${({ theme }) => theme.breakpoint.md}) {
    border-radius: 0;
  }
`

export const StyledColumnLayoutColumnWrapper = styled.div`
  flex: 1;
  flex-basis: 50%;
  display: flex;
  position: relative;
  gap: ${({ theme }) => theme.spacing.lg}rem;
  padding: ${({ theme }) => theme.spacing.md}rem;
  padding-top: 0;
`

export const StyledColumnLayoutColumn = styled.div<{
  $fullWidth: boolean
  $isRight: boolean
  $isOverlayMode: boolean
}>`
  ${({ $fullWidth, $isRight, $isOverlayMode }) => {
    if ($isOverlayMode && !$isRight) {
      return `
        flex: 1 1 100%;
        min-width: 0;
      `
    }

    if ($isOverlayMode && $isRight) {
      // A zero-width flex item still consumes `gap`, which insets the list
      // on the right. Leave the flex flow; the detail card is position:fixed.
      return `
        position: absolute;
        width: 0;
        height: 0;
        min-width: 0;
        overflow: visible;
        pointer-events: none;
      `
    }

    if (!$isOverlayMode && $fullWidth && $isRight) {
      return `
        flex: 1 0 54%;
        min-width: 24rem;
      `
    }

    if (!$isOverlayMode && $fullWidth && !$isRight) {
      return `
        flex: 1 1 46%;
        min-width: 20rem;
      `
    }

    return $fullWidth ? `flex-basis: 100%;` : `flex-basis: 50%;`
  }}
  padding-top: 0 !important;
`

export const StyledColumnLayoutColumnContentWrapper = styled.div`
  position: relative;
  height: 100%;
  width: 100%;
`

type ColumnContentStyleProps = {
  $flushSide?: 'none' | 'left' | 'right'
  $isOverlayMode?: boolean
  $isRight?: boolean
  $isMobile?: boolean
}

const isFloatingDetailCard = ({ $isOverlayMode, $isRight }: ColumnContentStyleProps) =>
  Boolean($isOverlayMode && $isRight)

export const StyledColumnLayoutColumnContent = styled(motion.div)<ColumnContentStyleProps>`
  position: ${props => (isFloatingDetailCard(props) ? 'fixed' : 'absolute')};
  top: ${props => (isFloatingDetailCard(props) ? 'auto' : '0')};
  right: ${props => (isFloatingDetailCard(props) ? 'auto' : '0')};
  bottom: auto;
  width: ${props => (isFloatingDetailCard(props) ? 'min(56rem, 52vw)' : '100%')};
  height: ${props => (isFloatingDetailCard(props) ? 'auto' : '100%')};
  min-width: 0;
  max-width: ${({ theme, ...props }) =>
    isFloatingDetailCard(props) ? `calc(100% - ${theme.spacing.md * 2}rem)` : '100%'};
  max-height: ${({ theme, ...props }) =>
    isFloatingDetailCard(props) ? `calc(100dvh - ${theme.spacing.md * 2}rem)` : '100%'};
  display: ${props => (isFloatingDetailCard(props) ? 'flex' : 'block')};
  flex-direction: column;
  ${props => (isFloatingDetailCard(props) ? 'pointer-events: auto;' : '')}
  border-radius: ${({ theme, $isMobile, ...props }) =>
    isFloatingDetailCard(props) && $isMobile ? '0' : `${theme.borderRadius.md}rem`};
  border-top-left-radius: ${({ $flushSide }) => ($flushSide === 'left' ? 0 : undefined)};
  border-bottom-left-radius: ${({ $flushSide }) => ($flushSide === 'left' ? 0 : undefined)};
  border-top-right-radius: ${({ $flushSide }) => ($flushSide === 'right' ? 0 : undefined)};
  border-bottom-right-radius: ${({ $flushSide }) => ($flushSide === 'right' ? 0 : undefined)};
  box-shadow: ${props =>
    isFloatingDetailCard(props) ? '0px 14px 40px rgba(15, 23, 42, 0.24)' : 'none'};
  overflow-x: hidden;
  overflow-y: hidden;
  z-index: ${props => (isFloatingDetailCard(props) ? FLOATING_DETAIL_CARD_Z : 'auto')};
`

export const StyledColumnLayoutOverlayBackdrop = styled(motion.div)`
  position: fixed;
  inset: 0;
  background: ${({ theme }) => theme.colors.backdrop};
  z-index: ${FLOATING_DETAIL_BACKDROP_Z};
`

export const StyledFloatingDetailCloseButton = styled.button`
  position: relative;
  width: 2rem;
  height: 2rem;
  border: 0;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.neutralBlack};
  background: rgba(255, 255, 255, 0.92);

  &:hover {
    background: white;
  }
`

export const StyledFloatingDetailHeader = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: ${({ theme }) => theme.spacing.sm}rem ${({ theme }) => theme.spacing.sm}rem 0;
  position: relative;
  background: white;
`

export const StyledFloatingDetailBody = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  background: white;
`
